import { useState } from 'react';
import type { LifecycleTrackedProposal, LifecycleAnalysisItem } from '../types/lifecycle';
import { LegislativePath, LegislativeStageBadge } from './LegislativePath';
import { SequencePanel } from './SequenceSurfacing';
import {
  formatDateTime,
  trackingLevelClass,
  trackingLevelLabel,
} from '../utils/lifecycleFormat';

interface TrackedDetailProps {
  item: LifecycleTrackedProposal;
  matchingAnalysis: LifecycleAnalysisItem | null;
  onClose: () => void;
  onJumpToAnalysis: (proposalId: string) => void;
}

export function TrackedDetail({ item, matchingAnalysis, onClose, onJumpToAnalysis }: TrackedDetailProps) {
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'failed'>('idle');

  // PR-E: explicit split of priority (axis 1, heuristic) vs. risk (axis 2, AI verdict).
  const hasAssessedRisk = item.risk_score != null && item.risk_level != null;
  const priorityScore = item.tracking_priority_score ?? item.tracking_score;
  const priorityLevel = item.tracking_priority_level ?? item.tracking_level;
  const headlineScore = hasAssessedRisk ? item.risk_score : priorityScore;
  const headlineLevel = hasAssessedRisk ? item.risk_level : priorityLevel;
  const headlineLabel = hasAssessedRisk ? 'Riziko' : 'Priorita';

  async function copyLink() {
    if (typeof window === 'undefined' || !navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopyStatus('copied');
      window.setTimeout(() => setCopyStatus('idle'), 1800);
    } catch {
      setCopyStatus('failed');
      window.setTimeout(() => setCopyStatus('idle'), 1800);
    }
  }

  return (
    <aside className="detail-panel detail-panel--tracked-mockup" aria-label="Detail sledovaného návrhu">
      <button type="button" className="detail-panel__back" onClick={onClose}>
        &larr; Zpět na seznam
      </button>

      <h2 className="detail-panel__title">{item.title}</h2>

      <div className="detail-panel__badges detail-panel__badges--headline">
        <LegislativeStageBadge status={item.legislative_status} fallbackLabel={item.current_stage_label} />
        <span className={`badge badge--risk-summary ${trackingLevelClass(headlineLevel)}`}>
          {headlineLabel}: {trackingLevelLabel(headlineLevel).toLocaleLowerCase('cs-CZ')} &middot; {headlineScore}
        </span>
      </div>

      {matchingAnalysis && (
        <div className="detail-panel__analysis-link detail-panel__analysis-link--compact">
          <button
            type="button"
            className="detail-panel__analysis-btn"
            onClick={() => onJumpToAnalysis(matchingAnalysis.proposal_id)}
          >
            Zobrazit AI analýzu
          </button>
        </div>
      )}

      <LegislativePath
        status={item.legislative_status}
        transition={item.last_transition ?? item.last_event?.transition ?? null}
      />

      <SequencePanel surfacing={item.sequence_surfacing} />

      <section className="detail-panel__section detail-panel__section--source">
        <h4>Zdroj</h4>
        <div className="detail-source-line">
          {item.detail_url ? (
            <a href={item.detail_url} target="_blank" rel="noopener noreferrer">{item.detail_url}</a>
          ) : (
            <span>PSP zdroj není k dispozici</span>
          )}
          <button type="button" className="detail-panel__copy" onClick={copyLink}>
            Kopírovat odkaz
          </button>
        </div>
        <div className="detail-source-line detail-source-line--muted">
          <span>První zachycení: {formatDateTime(item.first_seen_at)}</span>
          <span>Aktualizováno: {formatDateTime(item.last_seen_at)}</span>
          {typeof item.events_count_30d === 'number' && <span>Události za 30 dní: {item.events_count_30d}</span>}
        </div>
        {copyStatus === 'copied' && <span className="detail-panel__copy-status">Odkaz zkopírován</span>}
        {copyStatus === 'failed' && <span className="detail-panel__copy-status">Kopírování se nepovedlo</span>}
      </section>
    </aside>
  );
}
